/* eslint-disable react/no-children-prop */
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { flushMeasurePerf, useQuery } from '@graphcommerce/graphql'
import { Image } from '@graphcommerce/image'
import { getCategoryStaticPaths } from '@graphcommerce/magento-category'
import {
  extractUrlQuery,
  FilterTypes,
  getFilterTypes,
  ProductFiltersDocument,
  ProductFiltersQuery,
  ProductListDocument,
  ProductListParams,
  ProductListParamsProvider,
  ProductListQuery,
} from '@graphcommerce/magento-product'
import { redirectOrNotFound, StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, MetaRobots } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Box, NoSsr, Paper } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LayoutNavigation,
  LayoutNavigationProps,
  ProductListItems,
  RowProduct,
  RowRenderer,
} from '../components'
import { CategoryBreadcrumb } from '../components/Category/CategoryBreadcrumb/CategoryBreadcrumb'
import { CategoryChildSlider } from '../components/Category/CategoryChildSlider'
import { CategoryMeta } from '../components/Category/CategoryMeta/CategoryMeta'
import { LayoutDocument } from '../components/Layout/Layout.gql'
import { ProductListFilters } from '../components/Product/ProductListFilters/ProductListFilters'
import { parseParams } from '../components/Product/ProductListItems/filteredProductList'
import { ProductListPagination } from '../components/Product/ProductListPagination/ProductListPagination'
import { ProductListPerPage } from '../components/Product/ProductListPerPage/ProductListPerPage'
import { ProductListSort } from '../components/Product/ProductListSort/ProductListSort'
import {
  AmastySortFieldsDocument,
  AmastySortFieldsQuery,
} from '../components/Product/ProductListSort/ProductListSort.gql'
import { ProductQuestionsAndAnswers } from '../components/Reviews/ProductQuestionsAndAnswers'
import { RichTextBox } from '../components/RichText/RichTextBox'
import { AmFAQCategoryDocument } from '../graphql/AmFAQCategory.gql'
import { CategoryPageDocument, CategoryPageQuery } from '../graphql/CategoryPage.gql'
import { useResetCheckout } from '../hooks/useResetCheckout'
import useWindowSize from '../hooks/useWindowSize'
import { graphqlSharedClient, graphqlSsrClient } from '../lib/graphql/graphqlSsrClient'

export type CategoryProps = CategoryPageQuery &
  HygraphPagesQuery &
  ProductListQuery &
  AmastySortFieldsQuery &
  ProductFiltersQuery & { filterTypes?: FilterTypes; params?: ProductListParams }
export type CategoryRoute = { url: string[] }
type GetPageStaticPaths = GetStaticPaths<CategoryRoute>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, CategoryProps, CategoryRoute>

function CategoryPage(props: CategoryProps) {
  const { categories, products, filters, params, filterTypes, pages, amOrderList } = props
  const { isMobile } = useWindowSize()
  useResetCheckout()

  const category = categories?.items?.[0]
  const isLanding = category?.display_mode === 'PAGE'
  const page = pages?.[0]
  const isCategory = params && category && products?.items && filterTypes
  const [hasScrolledDown, setHasScrollDown] = useState(false)
  const productListRef = useRef(null)

  // scroll to the top of the page whenever a new page is loaded
  useEffect(() => {
    window.scrollTo(0, 0)
  })

  const categoryFAQs =
    useQuery(AmFAQCategoryDocument, {
      fetchPolicy: 'cache-and-network',
      variables: {
        pageSize: 100,
      },
    })?.data?.searchAmFaqCategories?.items ?? []

  const qaData = category
    ? categoryFAQs
        .filter((item) => item?.url_key?.replaceAll('_', '/') === category?.url_path)
        ?.at(0)?.questions
    : []

  const handleScrollToProductList = () => {
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (productListRef?.current as any)?.offsetTop - 70,
      behavior: 'smooth',
    })
  }

  const heading = useMemo(() => {
    if (category) {
      return (
        <NoSsr>
          <Paper elevation={6} className='px-10 py-5'>
            <div className='flex flex-col gap-[20px] lg:grid lg:grid-cols-2'>
              <div className='flex flex-col gap-4'>
                <h1 className='text-[30px]'>
                  {category?.h1_content ?? category?.name ?? page.title}
                </h1>
                {category?.description && category?.description?.length > 0 && (
                  <RichTextBox htmlContent={category?.description} />
                )}
              </div>
              {category.image && (
                <Box
                  sx={{
                    overflow: 'hidden',
                    height: '100%',
                    width: '100%',
                    backgroundRepeat: 'no-repeat',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '6px',
                  }}
                >
                  <Image
                    src={`${category.image ?? ''}`}
                    alt={`${category.image_alt ?? ''}`}
                    unoptimized
                    height={30}
                    width={60}
                    layout='responsive'
                    className='aspect-auto h-auto max-h-[150px] w-full rounded-[6px] object-cover'
                  />
                </Box>
              )}
            </div>
            {category?.children && category?.children?.length > 0 && (
              <NoSsr>
                <CategoryChildSlider children={category?.children} className='mt-10' />
              </NoSsr>
            )}
          </Paper>
        </NoSsr>
      )
    }
    return null
  }, [category, page])

  return (
    <div className='m-auto mt-0 lg:max-w-maxContentWidth 2xl:px-0'>
      <CategoryMeta
        params={params}
        title={page?.metaTitle ?? page?.title}
        metaDescription={page?.metaDescription}
        metaRobots={[] as MetaRobots[]}
        canonical={page?.url ? `/${page.url}` : undefined}
        {...category}
      />
      {!isMobile && category && (
        <div className='ml-[10px]'>
          <CategoryBreadcrumb category={category} />{' '}
        </div>
      )}

      {isCategory && !isLanding && (
        <ProductListParamsProvider value={params}>
          <div>
            {isMobile && heading}
            <div className='mb-[50px] bg-lightest-gray md:mr-[20px] md:flex md:gap-[20px]'>
              <div>
                {!isMobile && (
                  <ProductListFilters
                    aggregations={filters?.aggregations}
                    filterTypes={filterTypes}
                    hideCategoryFilter
                  />
                )}
              </div>
              <div ref={productListRef}>
                {!isMobile && heading}
                <Paper elevation={3} className='mt-[30px] p-[20px] pt-2 md:mt-[30px]'>
                  <div className='m-[10px] flex items-center justify-between md:py-[10px] lg:pb-[15px] lg:pt-[0px]'>
                    <ProductListSort
                      sort_fields={{
                        ...products.sort_fields,
                        options: amOrderList
                          ? amOrderList.map((option) => ({
                              __typename: 'SortField',
                              label: option?.text,
                              value: option?.attribute,
                            }))
                          : products?.sort_fields?.options,
                      }}
                      total_count={products?.total_count}
                      isCollectionPage
                    />
                    {!isMobile && <ProductListPerPage />}
                  </div>
                  <ProductListItems
                    title={category.name ?? ''}
                    items={products?.items}
                    loadingEager={1}
                    columns={3}
                  />
                  <div className='mt-[40px]'>
                    <ProductListPagination page_info={products?.page_info} params={params} />
                  </div>
                </Paper>
                {isMobile && (
                  <Paper elevation={3} className='mt-[30px] md:mt-[40px] md:p-[20px]'>
                    <ProductListFilters
                      aggregations={filters?.aggregations}
                      filterTypes={filterTypes}
                      hideCategoryFilter
                    />
                  </Paper>
                )}
                {category?.extra_description && category?.extra_description.length > 0 && (
                  <NoSsr>
                    <Paper
                      elevation={3}
                      className='md:px-15 mt-[30px] p-10 py-4 md:mt-[40px] md:py-4'
                    >
                      <RichTextBox htmlContent={category?.extra_description} />
                    </Paper>
                  </NoSsr>
                )}
                <NoSsr>
                  <Paper elevation={3} className='md:px-15 mt-[30px] p-10 md:mt-[40px] md:py-8'>
                    <h2 className='mb-8 text-[30px]'>{i18n._('Customer questions & answers')}</h2>
                    <ProductQuestionsAndAnswers qaData={qaData ?? []} displayInTab={false} />
                  </Paper>
                </NoSsr>
              </div>
            </div>
          </div>
        </ProductListParamsProvider>
      )}

      {page && (
        <RowRenderer
          content={page.content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...products?.items?.[0]}
                items={products?.items?.slice(0, 8)}
              />
            ),
          }}
        />
      )}
    </div>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}
CategoryPage.pageOptions = pageOptions

export default CategoryPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  // Disable getStaticPaths while in development mode
  if (process.env.NODE_ENV === 'development' || process.env.SKIP_ISG)
    return { paths: [], fallback: 'blocking' }

  const path = (loc: string) => getCategoryStaticPaths(graphqlSsrClient(loc), loc)
  const paths = (await Promise.all(locales.map(path))).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const [url, query] = extractUrlQuery(params)
  if (!url || !query) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const filterTypes = getFilterTypes(client)

  const staticClient = graphqlSsrClient(locale)

  const categoryPage = staticClient.query({
    query: CategoryPageDocument,
    variables: { url },
  })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const productListParams = parseParams(url, query, await filterTypes)
  const filteredCategoryUid = productListParams && productListParams.filters.category_uid?.in?.[0]

  const category = categoryPage.then((res) => res.data.categories?.items?.[0])
  let categoryUid = filteredCategoryUid
  if (!categoryUid) {
    categoryUid = (await category)?.uid ?? ''
    if (productListParams) productListParams.filters.category_uid = { in: [categoryUid] }
  }

  const defaultSortBy = (await conf).data.storeConfig?.catalog_default_sort_by

  if (
    defaultSortBy &&
    productListParams &&
    typeof productListParams.sort === 'object' &&
    Object.keys(productListParams.sort)
  ) {
    productListParams.sort = {
      [defaultSortBy]: 'DESC',
    }
  }

  const pages = hygraphPageContent(staticClient, url, category)
  const hasPage = filteredCategoryUid ? false : (await pages).data.pages.length > 0
  const hasCategory = Boolean(productListParams && categoryUid)

  if (!productListParams || !(hasPage || hasCategory))
    return redirectOrNotFound(staticClient, conf, params, locale)

  if (!hasCategory) {
    return {
      props: {
        ...(await categoryPage).data,
        ...(await pages).data,
        ...(await layout).data,
        apolloState: await conf.then(() => client.cache.extract()),
      },
      revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
    }
  }

  const filters = staticClient.query({
    query: ProductFiltersDocument,
    variables: { filters: { category_uid: { eq: categoryUid } } },
  })
  const products = staticClient.query({
    query: ProductListDocument,
    variables: {
      pageSize: (await conf).data.storeConfig?.grid_per_page ?? 24,
      ...productListParams,
      filters: { ...productListParams.filters, category_uid: { eq: categoryUid } },
    },
  })

  if ((await products).errors) return { notFound: true }

  const { category_name, category_url_path } =
    (await categoryPage).data.categories?.items?.[0]?.breadcrumbs?.[0] ?? {}

  const up =
    category_url_path && category_name
      ? { href: `/${category_url_path}`, title: category_name }
      : { href: `/`, title: 'Home' }

  const amSortFields = staticClient.query({
    query: AmastySortFieldsDocument,
    variables: {},
  })

  const result = {
    props: {
      ...(await categoryPage).data,
      ...(await products).data,
      ...(await pages).data,
      ...(await filters).data,
      ...(await layout).data,
      ...(await amSortFields).data,
      filterTypes: await filterTypes,
      params: productListParams,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
  }
  flushMeasurePerf()
  return result
}
